import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <div className="max-w-2xl mx-auto px-5 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 mb-6 min-h-[44px]" data-testid="terms-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to app
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-6 h-6 text-sky-500" />
          <h1 className="text-2xl font-bold text-slate-900">Terms of Service</h1>
        </div>

        <p className="text-sm text-slate-500 mb-8">Effective Date: April 1, 2026 &middot; AK Enterprise Inc.</p>

        <div className="space-y-8 text-sm text-slate-700 leading-relaxed">
          <Section title="Acceptance of terms">
            <p>By accessing or using BirthdayDeaLight ("the App"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the App. The App is operated by AKEnterprise Inc. ("we," "us," or "our").</p>
            <p>We may update these terms from time to time. Continued use of the App after changes are posted constitutes your acceptance of the revised terms.</p>
          </Section>

          <Section title="User responsibilities">
            <p>You are responsible for maintaining the security of your account credentials. You agree to provide accurate information when creating your account, including your birthday, which is used to show you relevant birthday deals.</p>
            <p>You agree to use the App in good faith. This includes submitting accurate food truck spot reports, honest wait time estimates, and truthful photo verifications. Submitting false information may result in account suspension.</p>
          </Section>

          <Section title="Prohibited uses">
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Create multiple accounts to claim the same offer more than once</li>
              <li>Submit false location reports, fake wait times, or fraudulent verifications</li>
              <li>Use automated scripts, bots, or scrapers to access the App</li>
              <li>Attempt to access other users' accounts or data</li>
              <li>Use the App for any unlawful purpose</li>
              <li>Reverse-engineer, decompile, or disassemble any part of the App</li>
              <li>Resell, redistribute, or commercially exploit birthday offers obtained through the App</li>
            </ul>
          </Section>

          <Section title="Birthday offers and deals">
            <p>BirthdayDeaLight is a discovery platform. All birthday offers, deals, and promotions listed in the App are provided by third-party businesses (restaurants, bakeries, food trucks, and other establishments). We aggregate and display these offers for your convenience.</p>
            <p><strong>We do not guarantee the availability, accuracy, or terms of any offer.</strong> Offers may change, expire, or be discontinued by the business at any time without notice. Terms and conditions of each offer are set by the offering business, not by BirthdayDeaLight.</p>
            <p>We make reasonable efforts to keep offer information current, but we are not responsible for offers that are no longer valid, have changed terms, or are unavailable when you visit a business.</p>
          </Section>

          <Section title="Food truck features">
            <p>Food truck locations, menus, wait times, and availability are crowd-sourced from our user community and self-reported by vendors. This information may not always be accurate or current. We are not responsible for food trucks that have moved, closed, or changed their offerings.</p>
            <p>Orders placed through the App are between you and the food truck vendor. We facilitate the ordering process but are not a party to the transaction.</p>
          </Section>

          <Section title="Points, badges, and gamification">
            <p>Points, badges, levels, and other gamification features have no monetary value and cannot be exchanged for cash. We reserve the right to adjust point values, badge criteria, or leaderboard rankings at any time.</p>
          </Section>

          <Section title="Limitation of liability">
            <p>To the maximum extent permitted by law, AK Enterprise Inc. and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the App.</p>
            <p>This includes but is not limited to: offers that are unavailable or different from what was listed, food quality or safety issues at any listed business, inaccurate location or wait time data, loss of points or account data, and any transactions between you and third-party businesses.</p>
            <p>Our total liability to you for any claims arising from your use of the App shall not exceed the amount you paid to us in the twelve months preceding the claim, or $100, whichever is less.</p>
          </Section>

          <Section title="Governing law">
            <p>These Terms of Service are governed by and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions. Any disputes arising from these terms or your use of the App shall be resolved in the state or federal courts located in California.</p>
          </Section>

          <Section title="Contact us">
            <p>If you have questions about these Terms of Service, contact us at:</p>
            <div className="mt-2 p-4 bg-slate-50 rounded-lg">
              <p className="font-semibold text-slate-800">AK Enterprise Inc.</p>
              <p>Email: <a href="mailto:hello@birthdaydealight.com" className="text-sky-500 underline">hello@birthdaydealight.com</a></p>
              <p>Website: <a href="https://birthdaydealight.com" className="text-sky-500 underline" target="_blank" rel="noopener noreferrer">birthdaydealight.com</a></p>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-base font-bold text-slate-800 mb-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
